import { createSlice } from '@reduxjs/toolkit';

// api client
import apiClient from './apiClient.js'

const formatArray = (arr) => `[${arr.map(e => `"${e}"`).join(', ')}]`;

const REQUESTS = {
  create: ({name, ownerIds, adminIds, projectNumber}, outputFields = `id`) => {
    // default: admins are users too
    return `mutation {
        createProject(project: {
          name: "${name}",
          ownerIds: ${formatArray(ownerIds)}, 
          adminIds: ${formatArray(adminIds)}, 
          projectNumber: "${projectNumber}"}){
          ${outputFields}
        }
      }`
  },
  index: ({userId}, outputFields = `id`) => {
    return `query {
      projects{
        ${outputFields}
      }
    }`
  },
  get: ({ key, value }, outputFields = `id`) => {
    return `query {
      project(input: {${key}: "${value}"}) {
        ${outputFields}
      }
    }`;
  },
  addMission: ({missionId, projectId}, outputFields = `id`) => {
    return `mutation {
      addMissionInProject(id: "${projectId}", missionId:"${missionId}")
    }`
  },
  addAdmin: ({userId, projectId}, outputFields = `id`) => {
    return `mutation {
      addAdminInProject(id: "${projectId}", adminIds: ["${userId}"])
    }`
  },
  removeAdmin: ({userId, projectId}, outputFields = `id`) => {
    return `mutation {
      removeAdminInProject(id: "${projectId}", adminIds: ["${userId}"])
    }`
  },
  addUser: ({userId, projectId}, outputFields = `id`) => {
    return `mutation {
      addUserInProject(id: "${projectId}", userIds: ["${userId}"])
    }`
  },
  removeUser: ({userId, projectId}, outputFields = `id`) => {
    return `mutation {
      removeUserInProject(id: "${projectId}", userIds: ["${userId}"])
    }`
  },
  update: ({id, name, projectNumber}, outputFields = `id`) => {
    return `mutation {
      editProject( project: {
        id: "${id}",
        name: "${name}",
        projectNumber: "${projectNumber}"
      }){
        ${outputFields}
      }
    }`
  }
}

const initialState = {
  all: []
}

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProjects: (state, {payload: {projects}}) => {
      return {
        ...state,
        // Newest first: API returns oldest→newest, reverse for home page display
        all: [...projects].reverse()
      }
    },
    setProjectId: (state, {payload}) => {
      return {
        ...state,
        projectId: payload
      }
    },
    createProject: (state, {payload}) => {
      return {
        ...state,
        all: [payload, ...state.all]
      }
    },
    addOfficeInProject: (state, {payload: {id, office}}) => {
      let newProjects = []
      for(let i=0; i < state.all.length; i++){
        if(state.all[i].id === id){
          newProjects.push({
            ...state.all[i],
            offices: [
              ...state.all[i].offices,
              office
            ]
          })
        }
      }

      return {
        ...state,
        all: newProjects
      }
    },
    addMissionInProject: (state, {payload: {id, user}}) => {
      let newProjects = []
      for(let i=0; i < state.all.length; i++){
        if(state.all[i].id === id){
          newProjects.push({
            ...state.all[i],
            userIds: [
              ...state.all[i].userIds,
              user
            ]
          })
        }
      }

      return {
        ...state,
        all: newProjects
      }
    },
    addUserInProject: (state, {payload: {id, user}}) => {
      let newProjects = []
      for(let i=0; i < state.all.length; i++){
        if(state.all[i].id === id){
          newProjects.push({
            ...state.all[i],
            userIds: [
              ...state.all[i].userIds,
              user
            ]
          })
        }
      }

      return {
        ...state,
        all: newProjects
      }
    },
    removeUserInProject: (state, {payload: {id, user}}) => {
      let newProjects = []
      for(let i=0; i < state.all.length; i++){
        if(state.all[i].id === id){
          newProjects.push({
            ...state.all[i],
            userIds: state.all[i].userIds.filter((el) => el.id !== user.id)
          })
        }
      }

      return {
        ...state,
        all: newProjects
      }
    }
  }
})

export const selectProjects = (state) => state.projects.all
export const selectProjectId = (state) => state.projects.projectId

export const { setProjects, setProjectId, createProject, addOfficeInProject, addMissionInProject, addUserInProject, removeUserInProject } = projectsSlice.actions


export const projectsApi = {
  create: async ({name, ownerIds, adminIds, projectNumber}, outputFields, next) => {
    console.log(ownerIds)
    console.log(adminIds)
    const result = await apiClient.req(REQUESTS.create({
      name,
      ownerIds,
      adminIds,
      projectNumber
    }, outputFields))
    next(result.data.data.createProject)
  },
  index: async ({userId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.index({userId}, outputFields))
    next(result.data.data)
  },
  get: async ({ id, inspectionId, missionId }, outputFields, next) => {
    const key = missionId ? 'missionId' : inspectionId ? 'inspectionId' : 'id';
    const value = id || missionId || inspectionId;
    const result = await apiClient.req(REQUESTS.get({ key, value}, outputFields));
    next(result.data.data);
  },
  addMissionInProject: async ({projectId, missionId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.addMission({projectId, missionId}, outputFields))
    next(result.data.data)
  },
  addUserInProject: async ({userId, projectId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.addUser({userId, projectId}, outputFields))
    next(result.data.data)
  },
  addAdminInProject: async ({userId, projectId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.addAdmin({userId, projectId}, outputFields))
    next(result.data.data)
  },
  removeAdminInProject: async ({userId, projectId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.removeAdmin({userId, projectId}, outputFields))
    next(result.data.data)
  },
  removeUserInProject: async ({userId, projectId}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.removeUser({userId, projectId}, outputFields))
    next(result.data.data)
  },
  update: async ({id, name, projectNumber}, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.update({id, name, projectNumber}, outputFields))
    next(result.data.data.editProject)
  }
}


export default projectsSlice.reducer;
